import React, { useState, useEffect } from 'react';
import api from '../services/api'; // Import the configured API client

function PDFUploader() {
  const [file, setFile] = useState(null);
  const [collection, setCollection] = useState('');
  const [collections, setCollections] = useState([]);
  const [customCollection, setCustomCollection] = useState('');
  const [uploading, setUploading] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  // Create a simple toast function as replacement
  const toast = (props) => {
    console.log("Toast:", props);
    // You could implement a custom toast here
  };

  // Load available collections
  useEffect(() => {
    async function fetchCollections() {
      try {
        const response = await api.get('/api/pdf-processing/collections/list');
        if (response.data.success) {
          setCollections(response.data.collections);
        }
      } catch (error) {
        console.error('Error fetching collections:', error);
        toast({
          title: 'Error fetching collections',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        // If API call fails, populate with mock collections for testing UI
        setCollections(['test_collection', 'sample_data', 'deliveries']);
      }
    }
    fetchCollections();
  }, []);

  // Poll for status updates
  useEffect(() => {
    let interval;
    if (taskId) {
      interval = setInterval(async () => {
        try {
          const response = await api.get(`/api/pdf-processing/${taskId}`);
          if (response.data.success) {
            setStatus(response.data);

            // Stop polling once completed or failed
            if (response.data.status === 'completed' || response.data.status === 'failed') {
              clearInterval(interval);

              // Show toast notification
              const message = response.data.message || '';
              // Filter out verbose technical messages from toast
              let displayMessage = message;
              if (message.includes('=== STEP') ||
                  message.includes('Vision API Processing') ||
                  message.includes('Application will restart to free memory') ||
                  message.includes('Starting processing') ||
                  message.includes('Start Processing')) {
                displayMessage = response.data.status === 'completed' ? 'Processing completed successfully' : 'Processing failed';
              }

              toast({
                title: response.data.status === 'completed' ? 'Processing completed' : 'Processing failed',
                description: displayMessage,
                status: response.data.status === 'completed' ? 'success' : 'error',
                duration: 5000,
                isClosable: true,
              });
            }
          }
        } catch (error) {
          console.error('Error checking status:', error);
          clearInterval(interval);
          setError('Error checking processing status');
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [taskId]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast({
        title: 'No file selected',
        description: 'Please select a PDF file to upload',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Determine which collection to use
    const collectionToUse = collection === 'new_collection' ? customCollection : collection;

    if (!collectionToUse) {
      toast({
        title: 'No collection selected',
        description: 'Please select or enter a MongoDB collection name',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setUploading(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('collection', collectionToUse);

    try {
      const response = await api.post('/api/pdf-processing', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setTaskId(response.data.task_id);
        toast({
          title: 'Upload successful',
          description: 'PDF processing has started',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        setError(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.response?.data?.message || error.message || 'Error uploading file');
      toast({
        title: 'Upload failed',
        description: error.response?.data?.message || error.message || 'Error uploading file',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFile(null);
    setTaskId(null);
    setStatus(null);
    setError(null);
    // Reset file input by clearing its value
    const fileInput = document.getElementById('file-upload');
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="w-full">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4">
          {error}
          <button
            className="float-right font-bold"
            onClick={() => setError(null)}
          >
            &times;
          </button>
        </div>
      )}

      {!taskId ? (
        <div className="bg-white p-6 rounded-md">
          {/* Removed title and description */}

          <div className={`p-8 border-2 border-dashed rounded-lg ${file ? 'border-green-500 bg-green-50' : 'border-gray-300'} mb-6`}>
            {!file ? (
              <div className="text-center space-y-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <div>
                  <h3 className="text-lg font-medium text-gray-700">
                    PDF-Datei auswählen
                  </h3>
                  <p className="mt-1 text-gray-500"></p>
                  <label className="mt-3 inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 cursor-pointer transition-colors">
                    Datei auswählen
                    <input
                      id="file-upload"
                      type="file"
                      className="hidden"
                      accept=".pdf"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-lg font-medium">{file.name}</p>
                    <p className="text-sm text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <button
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => setFile(null)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="collection" className="block text-sm font-medium text-gray-700 mb-1">
                Projekt zuweisen
              </label>

              {collection !== 'new_collection' ? (
                <div className="relative">
                  <select
                    id="collection"
                    value={collection}
                    onChange={(e) => setCollection(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                    required
                  >
                    <option value="" disabled>Projekt auswählen</option>
                    {collections
                      .filter(col => col.startsWith('A'))
                      .map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))
                    }
                    <option value="new_collection">Neues Projekt</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                    </svg>
                  </div>
                </div>
              ) : (
                <div className="flex">
                  <input
                    id="custom-collection"
                    value={customCollection}
                    onChange={(e) => setCustomCollection(e.target.value)}
                    placeholder="Projektname eingeben"
                    className="flex-grow block w-full border border-gray-300 rounded-l-md p-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <button
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium px-2 rounded-r-md border border-l-0 border-gray-300"
                    onClick={() => {
                      setCollection('');
                      setCustomCollection('');
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            <div className="pt-4">
              <div className="flex justify-end">
                <button
                  onClick={handleSubmit}
                  className="bg-green-500 hover:bg-green-600 text-white font-medium py-1.5 px-3 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!file || (collection === 'new_collection' && !customCollection) || uploading}
                >
                  {uploading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Wird hinzugefügt...
                    </span>
                  ) : 'Hinzufügen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white p-6 rounded-md">
          <div className="flex flex-col space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold text-gray-800">Processing PDF</h3>
              {status?.status === 'completed' && (
                <span className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                  Completed
                </span>
              )}
              {status?.status === 'processing' && (
                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing
                </span>
              )}
              {status?.status === 'failed' && (
                <span className="bg-red-100 text-red-800 text-sm font-medium px-3 py-1 rounded-full">
                  Failed
                </span>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {(() => {
                    const message = status?.message || 'Processing...';
                    // Filter out verbose technical messages
                    if (message.includes('=== STEP') ||
                        message.includes('Vision API Processing') ||
                        message.includes('Application will restart to free memory') ||
                        message.includes('Starting processing') ||
                        message.includes('Start Processing')) {
                      return 'Processing...';
                    }
                    return message;
                  })()}
                </span>
                <span className="font-medium">{status?.progress || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full ${
                    status?.status === 'completed' ? 'bg-green-500' :
                    status?.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${status?.progress || 0}%` }}
                ></div>
              </div>
            </div>

            {status?.status === 'completed' && status?.result && (
              <div className="mt-4 border border-gray-200 rounded-md overflow-hidden">
                <div className="bg-gray-100 px-4 py-2 font-medium">Ergebnisse der Auswertung</div>
                <div className="p-4 bg-white">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-md">
                      <p className="text-sm text-gray-500">Seitenanzahl der PDF-Datei</p>
                      <p className="text-xl font-semibold">{status.result.total_pages || 0}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-md">
                      <p className="text-sm text-gray-500">Anzahl der Lieferscheine</p>
                      <p className="text-xl font-semibold">{status.result.processed_pages || 0}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-md">
                      <p className="text-sm text-gray-500">Erfolgreich ausgewertet</p>
                      <p className="text-xl font-semibold text-green-600">{status.result.json_converted || 0}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-md">
                      <p className="text-sm text-gray-500">Projekt zugeordnet</p>
                      <p className="text-xl font-semibold">{status.result.collection_name || '--'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={resetForm}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-2 px-4 rounded-md transition-colors self-center"
            >
              Weitere Datei auswerten
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default PDFUploader;